import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Upload, Trash2, Image, Loader2, Copy, Check } from 'lucide-react';
import { resolveUrl } from '@/components/ImagePicker';

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

export default function AdminGallery() {
  const { getAuthHeader } = useAuth();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const response = await fetch(`${API}/admin/gallery`, {
        headers: { 'Authorization': getAuthHeader() }
      });
      if (response.ok) {
        const data = await response.json();
        setImages(data);
      }
    } catch (error) {
      console.error('Error:', error);
    }
    setLoading(false);
  };

  const handleUpload = async (files) => {
    if (!files?.length) return;
    setUploading(true);

    const formData = new FormData();
    for (const file of files) {
      formData.append('files', file);
    }

    try {
      const response = await fetch(`${API}/admin/upload-multiple`, {
        method: 'POST',
        headers: { 'Authorization': getAuthHeader() },
        body: formData,
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`${data.length} photo(s) uploadée(s)`);
        fetchImages();
      } else {
        toast.error('Erreur lors de l\'upload');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
    setUploading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette photo ?')) return;
    try {
      const response = await fetch(`${API}/admin/gallery/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': getAuthHeader() }
      });
      if (response.ok) {
        toast.success('Photo supprimée');
        fetchImages();
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const copyUrl = (url) => {
    const fullUrl = resolveUrl(url);
    navigator.clipboard.writeText(fullUrl);
    setCopiedId(url);
    toast.success('URL copiée');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    if (files.length) handleUpload(files);
  };

  const formatSize = (bytes) => {
    if (!bytes) return '?';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const totalOriginal = images.reduce((sum, img) => sum + (img.original_size || img.size || 0), 0);
  const totalOptimized = images.reduce((sum, img) => sum + (img.size || 0), 0);
  const totalSavings = totalOriginal > 0 ? Math.round((1 - totalOptimized / totalOriginal) * 100) : 0;

  return (
    <div className="min-h-screen bg-cream">
      <header className="bg-forest text-white py-4 px-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard" className="hover:text-sand">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="font-heading text-xl font-bold">Galerie Photos</h1>
            <span className="bg-white/20 px-2 py-0.5 rounded-full text-sm">{images.length} photos</span>
          </div>
          {images.length > 0 && totalSavings > 0 && (
            <div className="flex items-center gap-2 text-sm">
              <span className="bg-green-500/20 text-green-100 px-3 py-1 rounded-full">
                -{totalSavings}% d'espace ({formatSize(totalOriginal - totalOptimized)} économisés)
              </span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Upload zone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-2xl p-8 text-center mb-8 transition-colors ${
            dragOver ? 'border-autumn bg-autumn/5' : 'border-forest/20 bg-white'
          }`}
          data-testid="gallery-upload-zone"
        >
          {uploading ? (
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="w-8 h-8 animate-spin text-autumn" />
              <p className="font-body text-forest/70">Upload en cours...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3">
              <Upload className="w-10 h-10 text-forest/30" />
              <p className="font-body text-forest/70">
                Glissez-déposez vos photos ici ou
              </p>
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                className="mt-1"
              >
                Parcourir les fichiers
              </Button>
              <p className="font-body text-xs text-forest/40">JPG, PNG, WebP, GIF - Max 10MB par photo</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => handleUpload(Array.from(e.target.files))}
              />
            </div>
          )}
        </div>

        {/* Gallery grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-forest/40" />
          </div>
        ) : images.length === 0 ? (
          <div className="text-center py-16">
            <Image className="w-16 h-16 text-forest/20 mx-auto mb-4" />
            <p className="font-body text-forest/50">Aucune photo dans la galerie</p>
            <p className="font-body text-sm text-forest/40 mt-1">Uploadez vos premières photos ci-dessus</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {images.map((img) => (
              <div key={img.id} className="group relative bg-white rounded-xl overflow-hidden shadow-soft" data-testid={`gallery-image-${img.id}`}>
                <div className="aspect-square">
                  <img
                    src={resolveUrl(img.url)}
                    alt={img.filename}
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Overlay on hover */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-end">
                  <div className="w-full p-2 translate-y-full group-hover:translate-y-0 transition-transform">
                    <p className="text-white text-xs truncate mb-1.5">{img.filename}</p>
                    <div className="flex gap-1">
                      <button
                        onClick={() => copyUrl(img.url)}
                        className="flex-1 bg-white/90 hover:bg-white text-forest text-xs py-1 px-2 rounded flex items-center justify-center gap-1"
                      >
                        {copiedId === img.url ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                        {copiedId === img.url ? 'Copié' : 'URL'}
                      </button>
                      <button
                        onClick={() => handleDelete(img.id)}
                        className="bg-red-500/90 hover:bg-red-500 text-white text-xs py-1 px-2 rounded flex items-center justify-center"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
                <div className="px-2 py-1.5 border-t flex justify-between items-center">
                  <p className="text-[10px] text-forest/40 font-body">{formatSize(img.size)}</p>
                  {img.original_size && img.original_size > img.size && (
                    <p className="text-[10px] text-green-600 font-body font-medium">
                      -{Math.round((1 - img.size / img.original_size) * 100)}%
                    </p>
                  )}
                  {img.width && (
                    <p className="text-[10px] text-forest/30 font-body">{img.width}x{img.height}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
